import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { LoginPage } from './features/auth/LoginPage';
import { RequireAuth } from './features/auth/RequireAuth';
import { SeleccionarTenantPage } from './features/auth/SeleccionarTenantPage';
import { Verify2FAPage } from './features/auth/Verify2FAPage';
import { MiSimulacionPage } from './features/mi-simulacion/MiSimulacionPage';
import { SesionPage } from './features/simulacion/SesionPage';

/**
 * Rutas de desarrollo del spike F0.6. Sin `RequireAuth` a propósito: el
 * avatar no depende del backend.
 *
 * **Sólo existen en desarrollo** (tanda 3). En `vite build`,
 * `import.meta.env.DEV` se reemplaza por `false`, la rama muerta se
 * elimina y los `import()` de las páginas no llegan al bundle. En
 * producción `/dev/avatar` cae al comodín, como cualquier ruta
 * inexistente.
 *
 * `/dev/avatar` **se conserva**: se promueve a herramienta interna de
 * calibración para el equipo de diseño, con auth y rol, cuando lleguen
 * los avatares variantes (`docs/08_CALIBRACION.md` §4). El resto de rutas
 * `/dev/*` y el instrumental de spike se retiran al cerrar F0.6.
 */
function rutasDeDesarrollo(): RouteObject[] {
  if (!import.meta.env.DEV) return [];
  const AvatarSpikePage = lazy(() =>
    import('./features/dev/AvatarSpikePage').then((m) => ({ default: m.AvatarSpikePage })),
  );
  const AvatarPerfPage = lazy(() =>
    import('./features/dev/AvatarPerfPage').then((m) => ({ default: m.AvatarPerfPage })),
  );
  const conCarga = (pagina: ReactNode) => <Suspense fallback={null}>{pagina}</Suspense>;
  return [
    { path: '/dev/avatar', element: conCarga(<AvatarSpikePage />) },
    { path: '/dev/perf', element: conCarga(<AvatarPerfPage />) },
  ];
}

/**
 * Routing. Pantallas: `/login`, `/tenant`, `/2fa`, `/mi-simulacion` y la
 * entrevista `/simulacion/:id` (`04_ARQUITECTURA.md` §8.3). Lab y
 * bitácora entran en F2.
 */
// Type explícito para evitar TS2742 (inferred type referencia un módulo
// interno transitive de react-router-dom).
export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  { path: '/', element: <Navigate to="/mi-simulacion" replace /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/tenant', element: <SeleccionarTenantPage /> },
  { path: '/2fa', element: <Verify2FAPage /> },
  {
    path: '/mi-simulacion',
    element: (
      <RequireAuth>
        <MiSimulacionPage />
      </RequireAuth>
    ),
  },
  {
    path: '/simulacion/:id',
    element: (
      <RequireAuth>
        <SesionPage />
      </RequireAuth>
    ),
  },
  ...rutasDeDesarrollo(),
  { path: '*', element: <Navigate to="/login" replace /> },
]);
